import type { Sprite } from 'pixi.js';
import type { FoxAttack } from '../../data/full/enracine';
import { clamp01, lerp } from '../../shared/scalar';
import { FOX } from '../../sim/full/attacks/fox';
import { rootsRadius } from '../../sim/full/gauges/racines';
import { OWL } from '../../sim/full/skills/hibou';
import type { PlayerState, PropPhase, PropState, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, hide, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import { FOX_GLOW, WAG, placeFox } from './enracine';
import {
  GROUND_PIECES,
  GROUND_ROOT,
  OWL_WING_AT,
  SPIRIT_PIECES,
  UNIT,
  groundPoint,
} from './enracine-paint';

const PIECES = { ...SPIRIT_PIECES, ...GROUND_PIECES };
type SpiritShapes = Readonly<Record<keyof typeof PIECES, Shape>>;

const LEAP = { lift: 8, grow: 0.3, hop: 0.6 } as const;
// Mirrors the sim's hop on the same prey.
const HOP_TICKS = 6;
const ORBIT = { along: 34, across: 22, behind: -10, perBeat: 2, calm: 0.5, size: 1.1 } as const;
const FLAP = { perBeat: 1.5, turn: 0.25, calm: 0.4 } as const;
const ORBIT_FADE = 40;
const GROUND = {
  count: 7,
  width: 0.8,
  alpha: 0.9,
  bark: 0x5a3a24,
  bud: 0.9,
  budBeat: 0.25,
  budRadius: 9,
};

interface FoxView {
  readonly shadow: Sprite;
  readonly glow: Sprite;
  readonly tail: Sprite;
  readonly body: Sprite;
}

interface OwlView {
  readonly shadow: Sprite;
  readonly wings: readonly [Sprite, Sprite];
  readonly body: Sprite;
}

interface RootsView {
  readonly roots: readonly Sprite[];
  readonly buds: readonly Sprite[];
}

interface Track {
  phase: PropPhase;
  was: PropPhase;
  target: number | undefined;
  fromX: number;
  fromY: number;
  hitsLeft: number;
  seen: number;
}

export function createSpirits(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const foxes = new Map<string, FoxAttack>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'fox') {
      foxes.set(classId, attack);
    }
  }
  const sheet = paintedLater((): Sheet<keyof typeof PIECES> => paintSheet(PIECES));
  const burster = createBurster(ctx, 32);
  const tracks = new Map<number, Track>();
  let foxViews: ViewPool<FoxView> | undefined;
  let owlViews: ViewPool<OwlView> | undefined;
  let rootViews: ViewPool<RootsView> | undefined;
  let image = 0;

  function foxPool(shapes: SpiritShapes): ViewPool<FoxView> {
    foxViews ??= new ViewPool<FoxView>(
      () => ({
        shadow: add(layers.weapons, shapes.spiritShadow),
        glow: add(layers.glow, textures.halo),
        tail: add(layers.weapons, shapes.foxTail),
        body: add(layers.weapons, shapes.foxBody),
      }),
      (view) => {
        hide(view.shadow, view.glow, view.tail, view.body);
      },
    );
    return foxViews;
  }

  function owlPool(shapes: SpiritShapes): ViewPool<OwlView> {
    owlViews ??= new ViewPool<OwlView>(
      () => ({
        shadow: add(layers.weapons, shapes.spiritShadow),
        wings: [add(layers.bubbles, shapes.owlWing), add(layers.bubbles, shapes.owlWing)],
        body: add(layers.bubbles, shapes.owlBody),
      }),
      (view) => {
        hide(view.shadow, ...view.wings, view.body);
      },
    );
    return owlViews;
  }

  function rootPool(shapes: SpiritShapes): ViewPool<RootsView> {
    rootViews ??= new ViewPool<RootsView>(
      () => ({
        roots: Array.from({ length: GROUND.count }, () => add(layers.dim, shapes.groundRoot)),
        buds: Array.from({ length: GROUND.count }, () => add(layers.glow, textures.halo)),
      }),
      (view) => {
        hide(...view.roots, ...view.buds);
      },
    );
    return rootViews;
  }

  function trackOf(prop: PropState): Track {
    let track = tracks.get(prop.id);
    if (track === undefined) {
      track = {
        phase: prop.phase,
        was: prop.phase,
        target: prop.targetId,
        fromX: prop.prevX,
        fromY: prop.prevY,
        hitsLeft: prop.hitsLeft,
        seen: image,
      };
      tracks.set(prop.id, track);
    }
    track.was = track.phase;
    if (track.phase !== prop.phase || track.target !== prop.targetId) {
      track.phase = prop.phase;
      track.target = prop.targetId;
      track.fromX = prop.prevX;
      track.fromY = prop.prevY;
    }
    track.seen = image;
    return track;
  }

  function leapShare(state: SimState, prop: PropState, track: Track, x: number, y: number) {
    if (prop.phase === 'flying') {
      return LEAP.hop * Math.sin(Math.PI * Math.min(1, 1 - prop.ticksLeft / HOP_TICKS));
    }
    if (prop.phase !== 'bouncing' || track.target === undefined) {
      return 0;
    }
    const target = byId(state.enemies, track.target);
    if (target === undefined) {
      return 0;
    }
    const gone = Math.hypot(x - track.fromX, y - track.fromY);
    const left = Math.hypot(target.x - x, target.y - y);
    return Math.sin(Math.PI * (gone / Math.max(1, gone + left)));
  }

  function drawFox(
    state: SimState,
    prop: PropState,
    owner: PlayerState,
    alpha: number,
    frame: Frame,
  ) {
    const { shapes } = sheet.get();
    const track = trackOf(prop);
    if (prop.hitsLeft < track.hitsLeft) {
      burster.spawn(frame, {
        shape: textures.ring,
        now: frame.now,
        duration: 6,
        x: prop.x,
        y: prop.y,
        fromRadius: 6,
        toRadius: 22,
        tint: frame.palette.tank,
        peak: 0.7,
      });
    }
    track.hitsLeft = prop.hitsLeft;
    if (prop.phase === 'held') {
      return;
    }
    const view = foxPool(shapes).acquire(prop.id);
    const { now, calm, light, palette, tempo } = frame;
    const x = lerp(prop.prevX, prop.x, alpha);
    const y = lerp(prop.prevY, prop.y, alpha);
    const size = owner.radius / REFERENCE_RADIUS;
    const leap = leapShare(state, prop, track, x, y) * (calm ? 0.5 : 1);
    const lift = LEAP.lift * UNIT * size * leap;
    const heading = headingOf(state, prop, owner, x, y);
    const beats = now / tempo.ticksPerBeat;
    const wag =
      Math.sin(TAU * WAG.perBeat * beats * (calm ? 0.5 : 1)) * (calm ? WAG.calm : WAG.turn);
    const grown = size * (1 + LEAP.grow * leap);
    for (const sprite of [view.shadow, view.glow, view.tail, view.body]) {
      sprite.visible = true;
    }
    view.shadow.position.set(x, y);
    view.shadow.scale.set(size * (1 - 0.3 * leap));
    view.shadow.alpha = 0.3 * (1 - 0.4 * leap);
    setTint(view.shadow, 0x000000);
    placeFox(view.body, view.tail, x, y - lift, heading, grown, wag);
    view.glow.position.set(x, y - lift);
    view.glow.scale.set((FOX_GLOW * UNIT * grown) / textures.halo.radius);
    view.glow.alpha = 0.35 * light.haloAlpha;
    setTint(view.glow, palette.tank);
  }

  function drawOwl(prop: PropState, owner: PlayerState, alpha: number, frame: Frame) {
    const { shapes } = sheet.get();
    const track = trackOf(prop);
    if (track.was === 'flying' && prop.phase === 'returning') {
      burster.spawn(frame, {
        shape: textures.ring,
        now: frame.now,
        duration: 8,
        x: prop.x,
        y: prop.y,
        fromRadius: 8,
        toRadius: 30,
        tint: frame.palette.turquoise,
        peak: 0.8,
      });
    }
    const view = owlPool(shapes).acquire(prop.id);
    const { now, calm, tempo } = frame;
    const beats = now / tempo.ticksPerBeat;
    const size = (owner.radius / REFERENCE_RADIUS) * ORBIT.size;
    const ownerX = lerp(owner.prevX, owner.x, alpha);
    const ownerY = lerp(owner.prevY, owner.y, alpha);
    const simX = prop.phase === 'held' ? ownerX : lerp(prop.prevX, prop.x, alpha);
    const simY = prop.phase === 'held' ? ownerY : lerp(prop.prevY, prop.y, alpha);
    const hover = Math.max(0, 1 - Math.hypot(simX - ownerX, simY - ownerY) / ORBIT_FADE);
    const turn = ORBIT.perBeat * beats * (calm ? ORBIT.calm : 1) + prop.id;
    const unit = UNIT * (owner.radius / REFERENCE_RADIUS);
    const x = simX + hover * Math.cos(turn) * ORBIT.along * unit;
    const y = simY + hover * (ORBIT.behind + Math.sin(turn) * ORBIT.across) * unit;
    const diving = prop.phase !== 'held' && hover < 1;
    const heading = diving
      ? Math.atan2(prop.y - prop.prevY, prop.x - prop.prevX) || turn + Math.PI / 2
      : turn + Math.PI / 2;
    const flap = Math.sin(TAU * FLAP.perBeat * beats * (calm ? 0.5 : 1)) * FLAP.turn;
    const beat = calm ? flap * FLAP.calm : flap;
    const cos = Math.cos(heading);
    const sin = Math.sin(heading);
    for (const [index, side] of [-1, 1].entries()) {
      const wing = view.wings[index];
      if (wing === undefined) {
        continue;
      }
      wing.visible = true;
      wing.position.set(
        x - sin * side * OWL_WING_AT * UNIT * size,
        y + cos * side * OWL_WING_AT * UNIT * size,
      );
      wing.rotation = heading + side * beat;
      wing.scale.set(size, side * size);
    }
    view.body.visible = true;
    view.body.position.set(x, y);
    view.body.rotation = heading;
    view.body.scale.set(size);
    view.shadow.visible = true;
    view.shadow.position.set(x + 6 * unit, y + 8 * unit);
    view.shadow.scale.set(size * 1.4, size * 0.7);
    view.shadow.alpha = 0.3;
    setTint(view.shadow, 0x000000);
  }

  function drawRoots(player: PlayerState, attack: FoxAttack, alpha: number, frame: Frame) {
    const value = player.gauge ?? 0;
    if (value <= 0 || player.downed) {
      return;
    }
    const { shapes } = sheet.get();
    const view = rootPool(shapes).acquire(player.id);
    const { now, calm, light, palette, tempo } = frame;
    const x = lerp(player.prevX, player.x, alpha);
    const y = lerp(player.prevY, player.y, alpha);
    const reach = rootsRadius(attack, value) * Math.min(1, value);
    const lit = clamp01(value - attack.roots.slowFrom + 1);
    const color = palette[lookup(ctx.classTokens, player.classId, 'class')];
    const breath = calm
      ? 1
      : 0.75 + 0.25 * Math.cos(TAU * GROUND.budBeat * (now / tempo.ticksPerBeat));
    view.roots.forEach((root, index) => {
      const bud = view.buds[index];
      if (bud === undefined) {
        return;
      }
      const angle = (index * TAU) / GROUND.count + player.id * 0.7 + 0.35 * Math.sin(index * 2.3);
      const length = reach * (0.78 + 0.22 * Math.abs(Math.sin(index * 1.7 + 0.4)));
      const across = (index % 2 === 0 ? 1 : -1) * GROUND.width;
      root.visible = true;
      root.position.set(x, y);
      root.rotation = angle;
      root.scale.set(length / GROUND_ROOT, across);
      root.alpha = GROUND.alpha;
      setTint(root, GROUND.bark);
      bud.visible = lit > 0;
      if (bud.visible) {
        const [, tip] = groundPoint(1);
        const tipAcross = tip * across;
        bud.position.set(
          x + Math.cos(angle) * length - Math.sin(angle) * tipAcross,
          y + Math.sin(angle) * length + Math.cos(angle) * tipAcross,
        );
        bud.scale.set(GROUND.budRadius / textures.halo.radius);
        bud.alpha = GROUND.bud * lit * breath * Math.max(0.6, light.haloAlpha);
        setTint(bud, color);
      }
    });
  }

  return {
    reset(): void {
      tracks.clear();
      burster.bursts.clear();
      foxViews?.releaseAll();
      owlViews?.releaseAll();
      rootViews?.releaseAll();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (foxes.size === 0) {
        return;
      }
      image += 1;
      const { shapes } = sheet.get();
      const foxesPool = foxPool(shapes);
      const owlsPool = owlPool(shapes);
      const rootsPool = rootPool(shapes);
      foxesPool.begin();
      owlsPool.begin();
      rootsPool.begin();
      for (const player of state.players) {
        const attack = foxes.get(player.classId);
        if (attack !== undefined) {
          drawRoots(player, attack, alpha, frame);
        }
      }
      for (const prop of state.props ?? []) {
        if (prop.kind !== FOX && prop.kind !== OWL) {
          continue;
        }
        const owner = byId(state.players, prop.playerId);
        if (owner === undefined) {
          continue;
        }
        if (prop.kind === FOX) {
          drawFox(state, prop, owner, alpha, frame);
        } else {
          drawOwl(prop, owner, alpha, frame);
        }
      }
      foxesPool.end();
      owlsPool.end();
      rootsPool.end();
      for (const [id, track] of tracks) {
        if (track.seen !== image) {
          tracks.delete(id);
        }
      }
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      sheet.painted?.source.destroy();
    },
  };
}

function headingOf(state: SimState, prop: PropState, owner: PlayerState, x: number, y: number) {
  const target = prop.targetId === undefined ? undefined : byId(state.enemies, prop.targetId);
  const to = target ?? (prop.phase === 'returning' ? owner : undefined);
  if (to === undefined || Math.hypot(to.x - x, to.y - y) < 1) {
    return Math.atan2(owner.aim.y, owner.aim.x);
  }
  return Math.atan2(to.y - y, to.x - x);
}
